import React, { useEffect, useState } from 'react';
import { LuLayers } from 'react-icons/lu';

import { Allocation, AllocationType, AllocationWithUsage, GUID, Strategy } from '@ndvr/portfolio-api';

import { LibrarySortKey } from '../LibraryFilterBar';
import { LibraryPagination } from '../LibraryPagination';
import { ModelCard } from './ModelCard';

interface ModelsSectionProps {
    allocations: AllocationWithUsage[];
    isLoading: boolean;
    hasSupportRole?: boolean;
    sort: LibrarySortKey;
    selectedGUIDs: Set<GUID<Allocation>>;
    onToggleSelect: (allocationGUID: GUID<Allocation>) => void;
    onEdit: (allocation: Allocation) => void;
    onClone: (allocation: Allocation) => void;
    onDelete: (allocation: AllocationWithUsage) => void;
    onOpenStrategy: (strategy: Strategy) => void;
}

const DEFAULT_PAGE_SIZE = 6;

export const ModelsSection: React.FC<ModelsSectionProps> = ({
    allocations,
    isLoading,
    hasSupportRole,
    sort,
    selectedGUIDs,
    onToggleSelect,
    onEdit,
    onClone,
    onDelete,
    onOpenStrategy,
}) => {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        setPageIndex(0);
    }, [allocations, sort]);

    const sortedAllocations = [...allocations].sort((a, b) =>
        sort === 'accounts'
            ? (b.referencingAccountCount ?? 0) - (a.referencingAccountCount ?? 0)
            : a.allocationName.localeCompare(b.allocationName),
    );
    const pagedAllocations = sortedAllocations.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    const canEditAllocation = (allocation: AllocationWithUsage) =>
        allocation.allocationType === AllocationType.PERSONAL ||
        (allocation.allocationType === AllocationType.SHARED && !!hasSupportRole);

    const getDeleteBlockedReason = (allocation: AllocationWithUsage): string | undefined => {
        if (allocation.allocationType === AllocationType.SHARED && !hasSupportRole) {
            return 'Only support users can delete shared models';
        }
        const referencingAccountCount = allocation.referencingAccountCount ?? 0;
        if (referencingAccountCount > 0) {
            return `Used by ${referencingAccountCount} ${referencingAccountCount === 1 ? 'account' : 'accounts'} — remove references first`;
        }
        return undefined;
    };

    if (sortedAllocations.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
                <LuLayers className="text-cocoa-500 h-4.5 w-4.5" />
                <h2 className="text-cocoa-1000 text-xs font-semibold tracking-[0.08em] uppercase">Models</h2>
                <span className="text-cocoa-500 text-xs">{sortedAllocations.length}</span>
                <div className="bg-cocoa-200 h-px flex-1" />
                <LibraryPagination
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalItems={sortedAllocations.length}
                    onPageIndexChange={setPageIndex}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPageIndex(0);
                    }}
                />
            </div>

            {isLoading && <div className="text-cocoa-500 text-sm">Loading models...</div>}
            {!isLoading && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {pagedAllocations.map((allocation) => (
                        <ModelCard
                            key={allocation.allocationGUID}
                            allocation={allocation}
                            canEdit={canEditAllocation(allocation)}
                            selected={selectedGUIDs.has(allocation.allocationGUID)}
                            deleteBlockedReason={getDeleteBlockedReason(allocation)}
                            onToggleSelect={() => onToggleSelect(allocation.allocationGUID)}
                            onEdit={() => onEdit(allocation)}
                            onClone={() => onClone(allocation)}
                            onDelete={() => onDelete(allocation)}
                            onOpenStrategy={onOpenStrategy}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
